import { imageDataToBase64 } from '@/common/images';
import { useGetUploadUrlMutation } from '@/common/utils/graphql.generated';
import { StandardButton } from '@/components/buttons';
import { Camera } from '@/components/camera/component';
import { ManualCodeEntry } from '@/components/manual-code-entry/component';
import { Scanner } from '@/components/scanner';
import { MobileContainer } from '@/components/workflow/mobile-container';
import { MobileHeader } from '@/components/workflow/mobile-header';
import { WorkflowFooter } from '@/components/workflow/workflow-footer';
import { cleanCode } from '@/utils';
import { Button, Flex, LoadingOverlay, Text, Title } from '@mantine/core';
import { useNavigate, useParams } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import React, { useCallback, useState } from 'react';
import { ScanEventChangeInput, ScanEventType } from '../../../../../backend/src/types.generated';
import { useFetchTrackableAssetsLazyQuery } from '../workflows/bale-create/graphql.generated';
import { BagConditionSection } from './bag-condition-section';
import { ScanEventChange } from './change-types';
import { hasChanges } from './change-utils';
import { DocketPhotoSection } from './docket-photo-section';
import {
	useAddEditScanEventPhotoMutation,
	useScanEventDetailsQuery,
	useUpdateScanEventMutation,
} from './graphql.generated';
import { LoadPhotosSection } from './load-photos-section';
import { MaterialSection } from './material-section';
import { EditScanPhotoService } from './photo-service';
import { ScanDetailsSection } from './scan-details-section';
import { ScannedItemsSection } from './scanned-items-section';
import { EditFormData, TrackableAssetItem, ViewState } from './types';

export const EditScanPage = () => {
	const navigate = useNavigate();
	const { scanId } = useParams({ from: '/facility/edit-scan/$scanId' });
	const [currentView, setCurrentView] = useState<ViewState>('form');
	const { data, loading, error } = useScanEventDetailsQuery({
		variables: {
			scanEventId: scanId as string,
		},
	});
	const [updateScanEvent, { loading: updateLoading }] = useUpdateScanEventMutation();

	const [getUploadUrlMutation] = useGetUploadUrlMutation();

	const [addScanEventPhotoMutation] = useAddEditScanEventPhotoMutation();

	// Create photo service instance
	const photoService = React.useMemo(
		() => new EditScanPhotoService(getUploadUrlMutation, addScanEventPhotoMutation),
		[getUploadUrlMutation, addScanEventPhotoMutation]
	);

	// Initialize form data from GraphQL response
	const [formData, setFormData] = useState<EditFormData>({
		docketId: null,
		weight: null,
		truckRegistration: null,
		material: null,
		contaminationAmount: null,
		isContaminated: false,
		isDamaged: false,
		notes: null,
		photos: [],
		docketPhoto: null,
		scannedItems: [],
	});

	// Track changes for API submission
	const [changes, setChanges] = useState<ScanEventChange[]>([]);

	// Helper to add a change to the tracker
	const addChange = useCallback((change: ScanEventChange) => {
		setChanges((prev) => [...prev, change]);
	}, []);

	// Toggle handlers for bag condition (contamination / damage)
	const handleToggleContaminated = () => {
		const newValue = !formData.isContaminated;
		setFormData((prev) => ({ ...prev, isContaminated: newValue }));
		addChange({ type: 'UPDATE_IS_CONTAMINATED', value: newValue });
	};

	const handleToggleDamaged = () => {
		const newValue = !formData.isDamaged;
		setFormData((prev) => ({ ...prev, isDamaged: newValue }));
		addChange({ type: 'UPDATE_IS_DAMAGED', value: newValue });
	};

	// Update form data when GraphQL data is loaded
	React.useEffect(() => {
		if (data?.scanEvent) {
			const scanEvent = data.scanEvent;
			const trackableAsset = scanEvent.trackableAsset;

			// Extract photos
			const loadPhotos =
				scanEvent.photos
					?.filter((p) => p.photoType === 'LOAD')
					?.map((p) => p.photo?.url)
					.filter((url): url is string => Boolean(url)) || [];
			const docketPhoto =
				scanEvent.photos?.find((p) => p.photoType === 'DOCKET')?.photo?.url || null;

			setFormData({
				docketId: trackableAsset?.docketId || null,
				weight: scanEvent.weight || null,
				truckRegistration: trackableAsset?.truckRegistration || null,
				material: scanEvent.material || null,
				contaminationAmount: scanEvent.contaminationAmount || null,
				isContaminated: scanEvent.isContaminated || false,
				isDamaged: scanEvent.isDamaged || false,
				notes: scanEvent.notes || null,
				photos: loadPhotos,
				docketPhoto: docketPhoto,
				scannedItems: trackableAsset?.containsTrackableAssets || [],
			});
		}
	}, [data]);

	const handleInput = (
		key: keyof EditFormData,
		value: string | number | boolean | string[] | null
	) => {
		// Update form data
		setFormData((prev) => ({ ...prev, [key]: value }));

		// Record the change for API submission
		switch (key) {
			case 'docketId':
				addChange({ type: 'UPDATE_DOCKET_ID', value: value as string | null });
				break;
			case 'weight':
				addChange({ type: 'UPDATE_WEIGHT', value: value as number | null });
				break;
			case 'truckRegistration':
				addChange({ type: 'UPDATE_TRUCK_REGISTRATION', value: value as string | null });
				break;
			case 'contaminationAmount':
				addChange({ type: 'UPDATE_CONTAMINATION_AMOUNT', value: value as number | null });
				break;
			case 'notes':
				addChange({ type: 'UPDATE_NOTES', value: value as string | null });
				break;
			// For other fields, we handle them in specific handlers
		}
	};

	const handleSave = async () => {
		try {
			// First, upload any pending photos
			const photoChanges = changes.filter(
				(c) => c.type === 'ADD_LOAD_PHOTO' || c.type === 'ADD_DOCKET_PHOTO'
			);

			if (photoChanges.length > 0) {
				// Upload all pending photos
				const uploadPromises = photoChanges.map((change) => {
					if (change.type === 'ADD_LOAD_PHOTO') {
						return photoService.uploadPhoto(change.imageData, scanId as string, 'LOAD');
					} else if (change.type === 'ADD_DOCKET_PHOTO') {
						return photoService.uploadPhoto(change.imageData, scanId as string, 'DOCKET');
					}
					return Promise.resolve({ success: false, error: 'Unknown photo type' });
				});

				const uploadResults = await Promise.all(uploadPromises);

				// Check if any uploads failed
				const failedUploads = uploadResults.filter((result) => !result.success);
				if (failedUploads.length > 0) {
					// eslint-disable-next-line no-console
					console.error('Some photo uploads failed:', failedUploads);
					// TODO: Show error toast to user
					return; // Don't proceed with save if photo uploads failed
				}
			}

			// Convert non-photo changes to the format expected by the GraphQL mutation
			const nonPhotoChanges = changes.filter(
				(c) => c.type !== 'ADD_LOAD_PHOTO' && c.type !== 'ADD_DOCKET_PHOTO'
			);
			const mutationChanges = nonPhotoChanges.map((change) => {
				const baseChange = {
					type: change.type,
				};

				switch (change.type) {
					case 'ADD_SCAN':
						return {
							...baseChange,
							scannableCode: change.scannableCode,
						};
					case 'REMOVE_SCAN':
						return {
							...baseChange,
							scanId: change.scanId,
						};
					case 'UPDATE_DOCKET_ID':
						return {
							...baseChange,
							value: change.value,
						};
					case 'UPDATE_WEIGHT':
						return {
							...baseChange,
							numberValue: change.value,
						};
					case 'UPDATE_TRUCK_REGISTRATION':
						return {
							...baseChange,
							value: change.value,
						};
					case 'UPDATE_CONTAMINATION_AMOUNT':
						return {
							...baseChange,
							numberValue: change.value,
						};
					case 'UPDATE_IS_CONTAMINATED':
						return {
							...baseChange,
							value: change.value.toString(),
						};
					case 'UPDATE_IS_DAMAGED':
						return {
							...baseChange,
							value: change.value.toString(),
						};
					case 'UPDATE_NOTES':
						return {
							...baseChange,
							value: change.value,
						};
					case 'REMOVE_LOAD_PHOTO':
						return {
							...baseChange,
							photoId: change.photoId,
						};
					case 'REMOVE_DOCKET_PHOTO':
						return {
							...baseChange,
						};
					default:
						return baseChange;
				}
			});

			// Only call backend mutation if there are non-photo changes
			if (mutationChanges.length > 0) {
				await updateScanEvent({
					variables: {
						input: {
							scanEventId: scanId as string,
							// TODO: Fix the types here
							changes: mutationChanges as ScanEventChangeInput[],
						},
					},
				});
			}

			// Navigate back to history page after successful save
			navigate({ to: '/facility/history' });
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('Error saving scan event:', err);
			// TODO: Show error toast/notification to user
		}
	};

	const handleAddScan = () => {
		setCurrentView('scanner');
	};

	const [fetchTrackableAssets] = useFetchTrackableAssetsLazyQuery();
	const [manualCodeEntryOpen, setManualCodeEntryOpen] = useState(false);
	const [manualCodeEntryValue, setManualCodeEntryValue] = useState('');
	const [manualCodeEntryError, setManualCodeEntryError] = useState<string | null>(null);
	const handleScanComplete = async (scannedCode: string) => {
		const { data } = await fetchTrackableAssets({
			variables: {
				filter: {
					scannableCode: cleanCode(scannedCode),
				},
			},
		});
		if (
			data?.trackableAssets?.length === 0 ||
			data?.trackableAssets?.[0]?.assetType !== 'PAYLOAD_TAG'
		) {
			return 'error';
		}
		const tempId = `temp-${Date.now()}`;

		// Create a new scanned item
		const newItem: TrackableAssetItem = {
			id: tempId,
			scannableCode: scannedCode.split('/').pop(),
			intendedMaterial: null,
			status: null,
			assetType: 'SCANNED_ITEM',
		};

		// Add to form data
		setFormData((prev) => ({
			...prev,
			scannedItems: [...prev.scannedItems, newItem],
		}));

		// Record the change
		addChange({ type: 'ADD_SCAN', scannableCode: scannedCode.split('/').pop() ?? '' });

		// Return to form view
		setCurrentView('form');
	};

	const handleRemoveItem = (itemId: string) => {
		// Update form data
		setFormData((prev) => ({
			...prev,
			scannedItems: prev.scannedItems.filter((item) => item.id !== itemId),
		}));

		// Record the change
		addChange({ type: 'REMOVE_SCAN', scanId: itemId });
	};

	const handleAddLoadPhotos = () => {
		setCurrentView('load-photos');
	};

	const handleAddDocketPhoto = () => {
		setCurrentView('docket-photo');
	};

	const handleDocketPhotoCapture = (imageData: ImageData) => {
		// Convert to base64 for display
		const base64 = imageDataToBase64(imageData, 'jpeg', 0.8);
		const dataUrl = `data:image/jpeg;base64,${base64}`;

		// Update form data for display
		setFormData((prev) => ({
			...prev,
			docketPhoto: dataUrl,
		}));

		// Track the photo addition for upload on save
		addChange({ type: 'ADD_DOCKET_PHOTO', imageData });

		// Return to form view
		setCurrentView('form');
	};

	const handleLoadPhotosCapture = (imageData: ImageData[]) => {
		// Convert all images to base64 for display
		const photoUrls = imageData.map((image) => {
			const base64 = imageDataToBase64(image, 'jpeg', 0.8);
			return `data:image/jpeg;base64,${base64}`;
		});

		// Update form data for display
		setFormData((prev) => ({
			...prev,
			photos: [...prev.photos, ...photoUrls],
		}));

		// Track photo additions for upload on save
		imageData.forEach((image) => {
			const tempId = `temp-photo-${Date.now()}-${Math.random()}`;
			addChange({ type: 'ADD_LOAD_PHOTO', imageData: image, tempId });
		});

		// Return to form view
		setCurrentView('form');
	};

	const handleRemoveDocketPhoto = () => {
		// Update form data
		setFormData((prev) => ({ ...prev, docketPhoto: null }));

		// Check if this is an existing server photo or newly added photo
		const existingDocketPhoto = data?.scanEvent?.photos?.find((p) => p.photoType === 'DOCKET');
		const pendingDocketPhoto = changes.find((c) => c.type === 'ADD_DOCKET_PHOTO');

		if (existingDocketPhoto && !pendingDocketPhoto) {
			// This is an existing server-side photo - track deletion
			addChange({ type: 'REMOVE_DOCKET_PHOTO' });
		} else if (pendingDocketPhoto) {
			// This is a newly added photo - remove the ADD change
			setChanges((prev) => prev.filter((c) => c.type !== 'ADD_DOCKET_PHOTO'));
		}
	};

	const handleDeleteLoadPhoto = (photoIndex: number) => {
		// Update form data by removing the photo at the specified index
		setFormData((prev) => ({
			...prev,
			photos: prev.photos.filter((_, index) => index !== photoIndex),
		}));

		// Determine if this is an existing server photo or a newly added photo
		const existingLoadPhotos = data?.scanEvent?.photos?.filter((p) => p.photoType === 'LOAD') || [];
		const numExistingPhotos = existingLoadPhotos.length;
		const pendingLoadPhotos = changes.filter((c) => c.type === 'ADD_LOAD_PHOTO');

		if (photoIndex < numExistingPhotos) {
			// This is an existing server-side photo - track deletion
			const photoToDelete = existingLoadPhotos[photoIndex];
			if (photoToDelete?.id) {
				addChange({ type: 'REMOVE_LOAD_PHOTO', photoId: photoToDelete.id });
			}
		} else {
			// This is a newly added photo - remove the corresponding ADD change
			const newPhotoIndex = photoIndex - numExistingPhotos;
			if (newPhotoIndex < pendingLoadPhotos.length) {
				const photoChangeToRemove = pendingLoadPhotos[newPhotoIndex];
				setChanges((prev) => prev.filter((c) => c !== photoChangeToRemove));
			}
		}
	};

	if (loading) return <LoadingOverlay visible={loading} />;

	if (error) return <Text c="red">Error loading scan details: {error.message}</Text>;

	const scanEvent = data?.scanEvent;
	const trackableAsset = scanEvent?.trackableAsset;

	// Show scanner view
	if (currentView === 'scanner') {
		return (
			<MobileContainer
				header={<MobileHeader onBack={() => setCurrentView('form')} title="Scan Item" />}
				footer={
					<>
						{!manualCodeEntryOpen && (
							<WorkflowFooter>
								<Button
									onClick={() => setCurrentView('form')}
									size="md"
									variant="filled"
									bg="grey"
									c="white"
									fullWidth
								>
									Cancel
								</Button>
								<Button
									onClick={() => setManualCodeEntryOpen(true)}
									size="md"
									variant="filled"
									bg="grey"
									c="white"
									fullWidth
								>
									Enter Code
								</Button>
							</WorkflowFooter>
						)}
					</>
				}
			>
				<Scanner onScan={handleScanComplete} />
				<ManualCodeEntry
					value={manualCodeEntryValue}
					setValue={setManualCodeEntryValue}
					open={manualCodeEntryOpen}
					onClose={() => setManualCodeEntryOpen(false)}
					actions={({ onClose }) => (
						<Button
							onClick={async () => {
								const result = await handleScanComplete(manualCodeEntryValue);
								if (result === 'error') {
									setManualCodeEntryError('Invalid code. Please try again.');
								} else {
									setManualCodeEntryError(null);
									setManualCodeEntryValue('');
									onClose();
								}
							}}
						>
							Submit
						</Button>
					)}
					error={manualCodeEntryError ?? undefined}
				/>
			</MobileContainer>
		);
	}

	// Show docket photo capture view
	if (currentView === 'docket-photo') {
		return (
			<div>
				<MobileHeader title="Take Docket Photo" onBack={() => setCurrentView('form')} />
				<Camera onTakePhoto={handleDocketPhotoCapture} />
			</div>
		);
	}

	// Show load photos capture view
	if (currentView === 'load-photos') {
		return (
			<div>
				<MobileHeader title="Take Load Photos" onBack={() => setCurrentView('form')} />
				<Camera multiple onTakePhoto={handleLoadPhotosCapture} />
			</div>
		);
	}

	// Show main form view
	return (
		<MobileContainer
			header={
				<MobileHeader onBack={() => navigate({ to: '/facility/history' })} title="Edit Scan" />
			}
			footer={
				<WorkflowFooter>
					<StandardButton
						variant="filled"
						fullWidth
						onClick={handleSave}
						disabled={!hasChanges(changes) || updateLoading}
						loading={updateLoading}
					>
						Save Details
					</StandardButton>
				</WorkflowFooter>
			}
		>
			<Flex direction="column" w="100%" gap="lg" pb="md">
				{/* Scanned Items Section - only show if this scan type supports scan items */}
				{trackableAsset?.assetType === 'LOAD' && (
					<ScannedItemsSection
						items={formData.scannedItems}
						onRemoveItem={handleRemoveItem}
						onScan={handleAddScan}
					/>
				)}

				<ScanDetailsSection
					formData={formData}
					onInput={handleInput}
					assetType={trackableAsset?.assetType}
				/>

				{/* Material Section */}

				{/* Bag Condition Section - only for bag audit scans */}
				{scanEvent?.scanEventType?.includes('BAG_AUDIT') && (
					<>
						<MaterialSection formData={formData} onInput={handleInput} />
						<BagConditionSection
							isContaminated={formData.isContaminated}
							isDamaged={formData.isDamaged}
							onToggleContaminated={handleToggleContaminated}
							onToggleDamaged={handleToggleDamaged}
							contaminationNotes={formData.notes ?? ''}
							setContaminationNotes={(notes) => handleInput('notes', notes)}
						/>
					</>
				)}

				{/* Load Photos Section */}
				{[ScanEventType.FacilityBagAudit, ScanEventType.FacilityBagAudit].includes(
					scanEvent?.scanEventType as ScanEventType
				) && (
					<LoadPhotosSection
						photos={formData.photos}
						assetType={trackableAsset?.assetType}
						onAddPhoto={handleAddLoadPhotos}
						onDeletePhoto={handleDeleteLoadPhoto}
					/>
				)}

				{/* Docket Photo Section */}
				{(formData.docketPhoto || scanEvent?.photos?.some((p) => p.photoType === 'DOCKET')) && (
					<DocketPhotoSection
						docketPhoto={formData.docketPhoto}
						onAddPhoto={handleAddDocketPhoto}
						onRemovePhoto={handleRemoveDocketPhoto}
					/>
				)}

				{Boolean(scanEvent?.scanEventChanges?.length) && (
					<Flex direction="column" w="100%" gap="sm">
						<Flex px="md" align="center" justify="space-between" w="100%">
							<Title order={2}>Change Log</Title>
						</Flex>

						<Flex px="md" direction="column" gap="md">
							{scanEvent?.scanEventChanges?.map((change) => (
								<Flex key={change.id} w="100%" justify="space-between" gap="sm">
									<Text size="sm">
										{change.user.contact.firstName} {change.user.contact.lastName}
									</Text>
									<Text size="sm">
										{DateTime.fromISO(change.createdAt).toLocaleString(DateTime.DATETIME_MED)}
									</Text>
								</Flex>
							))}
						</Flex>
					</Flex>
				)}
			</Flex>
		</MobileContainer>
	);
};
